import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Box, X } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Button, Input, EmptyState, Pill, Spinner, useToast } from "@/components/earnos/kit";
import { colors, money } from "@/lib/theme";

const TYPES = ["digital_product", "template", "subscription", "recurring_service", "storefront", "content_library", "software", "other"];
const AUTO = ["manual", "lower_touch", "automated"];
const AUTO_LABEL = { manual: "Manual", lower_touch: "Lower-touch", automated: "Automated revenue" };

export default function IncomeAssets() {
  const navigate = useNavigate();
  const toast = useToast();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [modal, setModal] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("digital_product");
  const [revenue, setRevenue] = useState("");
  const [auto, setAuto] = useState("manual");
  const [saving, setSaving] = useState(false);
  const mutationLock = useRef(false);

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try { const d = await apiFetch("/assets"); setAssets(d.assets); } catch (e) { setLoadError(e.message || "Service unavailable. Please retry."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!name.trim()) { toast.show("Name your asset", "error"); return; }
    if (mutationLock.current) return;
    const value = revenue === "" ? null : Number(revenue);
    if (value !== null && (!Number.isFinite(value) || value < 0)) { toast.show("Revenue must be zero or a positive amount", "error"); return; }
    mutationLock.current = true; setSaving(true);
    try {
      await apiFetch("/assets", { method: "POST", body: { name: name.trim(), asset_type: type, revenue: value, automation_level: auto } });
      toast.show("Asset added", "success");
      setModal(false); setName(""); setRevenue(""); setType("digital_product"); setAuto("manual");
      load();
    } catch (e) { toast.show(e?.message || "Failed", "error"); }
    finally { mutationLock.current = false; setSaving(false); }
  };

  const del = async (id) => {
    if (mutationLock.current || !window.confirm("Permanently delete this income asset?")) return;
    mutationLock.current = true;
    try { await apiFetch(`/assets/${id}`, { method: "DELETE" }); await load(); }
    catch (e) { toast.show(e.message || "Could not delete this asset", "error"); }
    finally { mutationLock.current = false; }
  };

  if (loadError && !loading) return <LoadError title="Income assets" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label="Go back" data-testid="assets-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Income Assets</h1>
        </div>
        <button onClick={() => setModal(true)} aria-label="Add income asset" data-testid="add-asset" className="text-brand-light"><Plus size={26} /></button>
      </div>

      {loading ? (
        <Spinner className="py-24" />
      ) : assets.length === 0 ? (
        <EmptyState icon={Box} title="No income assets yet" message="Digital products, templates and recurring services you own live here — lower-touch income you build once." actionTitle="Add an asset" onAction={() => setModal(true)} />
      ) : (
        <div className="space-y-3">
          {assets.map((a) => (
            <div key={a.asset_id} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4">
              <div className="w-10 h-10 rounded-xl bg-brand/20 flex items-center justify-center shrink-0"><Box size={20} className="text-brand-light" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-ink">{a.name}</p>
                <p className="text-sm text-ink-2 capitalize">{a.asset_type.replace(/_/g, " ")}</p>
                <div className="flex gap-2 mt-1.5">
                  <Pill label={AUTO_LABEL[a.automation_level] || a.automation_level} color={colors.info} />
                  {a.is_demo ? <Pill label="Demo" color={colors.warning} /> : null}
                </div>
              </div>
              {a.revenue ? <span className="text-lg font-extrabold text-ok">{money(a.revenue)}</span> : null}
              <button onClick={() => del(a.asset_id)} aria-label={`Delete ${a.name}`} data-testid={`asset-del-${a.asset_id}`} className="text-ink-2 ml-1"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setModal(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="asset-entry-title" className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-surface-2 border border-line p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 id="asset-entry-title" className="text-xl font-bold text-ink">Add Income Asset</h3><button type="button" aria-label="Close asset form" disabled={saving} onClick={() => setModal(false)} className="text-ink"><X size={24} /></button></div>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reel Templates Pack" testID="asset-name" />
            <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mb-2">Type</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {TYPES.map((t) => <button key={t} onClick={() => setType(t)} className={`h-9 px-3 rounded-full text-sm font-semibold border capitalize ${type === t ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3"}`}>{t.replace(/_/g, " ")}</button>)}
            </div>
            <Input label="Revenue so far ($)" type="number" value={revenue} onChange={(e) => setRevenue(e.target.value)} placeholder="Optional" />
            <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mb-2">Automation</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {AUTO.map((a) => <button key={a} onClick={() => setAuto(a)} className={`h-9 px-4 rounded-full text-base font-semibold border ${auto === a ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3"}`}>{AUTO_LABEL[a]}</button>)}
            </div>
            <Button title="Save Asset" onClick={save} disabled={!name.trim()} loading={saving} full testID="asset-save" />
          </div>
        </div>
      )}
    </div>
  );
}
